import { CSSProperties, PropsWithChildren, ReactNode } from "react"
import {
    Box,
    DialogActions,
    DialogActionsProps,
    DialogContent,
    DialogContentProps,
    DialogContentText,
    DialogTitle,
    Drawer,
    DrawerProps,
    IconButton,
    Typography,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Remark } from "react-remark"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/pro-light-svg-icons/faTimes"
import { ErrorBoundaryModal } from "../errors/ErrorBoundaryModal"
import { faCircleXmark } from "@fortawesome/pro-thin-svg-icons"

type PaddingProps = Partial<
    Readonly<{
        top: CSSProperties["paddingTop"]
        bottom: CSSProperties["paddingBottom"]
        left: CSSProperties["paddingLeft"]
        right: CSSProperties["paddingRight"]
    }>
>

type StyleProps = Readonly<{
    header?: {
        variant?: StandardDrawerProps["headerVariant"]
        padding?: PaddingProps
    }
    content?: {
        padding?: PaddingProps
    }
    actions?: {
        padding?: PaddingProps
    }
}>

const DEFAULT_HORIZONTAL_PADDING = 4

const useStandardDrawerStyles = makeStyles<StyleProps>()((theme, { header, content, actions }) => ({
    drawerPaper: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    drawerHeader: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: header?.variant === "plain" ? "center" : "space-between",
        alignItems: "center",
        paddingTop: header?.padding?.top !== undefined ? header.padding.top : theme.spacing(3),
        paddingBottom: header?.padding?.bottom !== undefined ? header.padding.bottom : theme.spacing(2),
        paddingLeft:
            header?.padding?.left !== undefined ? header.padding.left : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
        paddingRight:
            header?.padding?.right !== undefined ? header.padding.right : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
    },
    drawerContent: {
        paddingTop: content?.padding?.top !== undefined ? content.padding.top : 0,
        paddingBottom: content?.padding?.bottom !== undefined ? content.padding.bottom : theme.spacing(3),
        paddingLeft:
            content?.padding?.left !== undefined ? content.padding.left : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
        paddingRight:
            content?.padding?.right !== undefined ? content.padding.right : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
    },
    drawerActions: {
        paddingTop: content?.padding?.top !== undefined ? content.padding.top : 0,
        paddingBottom: content?.padding?.bottom !== undefined ? content.padding.bottom : theme.spacing(3),
        paddingLeft:
            content?.padding?.left !== undefined ? content.padding.left : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
        paddingRight:
            content?.padding?.right !== undefined ? content.padding.right : theme.spacing(DEFAULT_HORIZONTAL_PADDING),
    },
}))

type StandardDrawerContentProps = DialogContentProps &
    Readonly<{
        padding?: PaddingProps
    }>

export function StandardDrawerContent({
    dividers = true,
    padding,
    className,
    children,
    ...rest
}: StandardDrawerContentProps) {
    const { classes, cx } = useStandardDrawerStyles({ content: { padding } })

    return (
        <DialogContent dividers={dividers} className={cx(classes.drawerContent, className)} {...rest}>
            {children}
        </DialogContent>
    )
}

type StandardDrawerActionsProps = DialogActionsProps &
    Readonly<{
        padding?: PaddingProps
    }>

export function StandardDrawerActions({ padding, className, children, ...rest }: StandardDrawerActionsProps) {
    const { classes, cx } = useStandardDrawerStyles({ actions: { padding } })

    return (
        <DialogActions className={cx(classes.drawerActions, className)} {...rest}>
            {children}
        </DialogActions>
    )
}

type StandardDrawerProps = Readonly<
    PropsWithChildren<{
        name: string
        title: string
        headerVariant?: "closeable" | "plain"
        headerPadding?: PaddingProps
        headerClassName?: string
        open: boolean
        onClose: () => void
        ["aria-describedby"]?: DrawerProps["aria-describedby"]
    }>
>

export function StandardDrawer(props: StandardDrawerProps) {
    const { name, title, headerVariant = "closeable", headerPadding, headerClassName, open, onClose, children } = props

    const { classes, cx } = useStandardDrawerStyles({ header: { variant: headerVariant, padding: headerPadding } })

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            aria-labelledby={`${name}-drawer-title`}
            PaperProps={{ square: false }}
            classes={{ paper: classes.drawerPaper }}
        >
            <ErrorBoundaryModal onClose={onClose}>
                <DialogTitle id={`${name}-drawer-title`} className={cx(classes.drawerHeader, headerClassName)}>
                    <Typography variant="h5">{title}</Typography>
                    {headerVariant === "closeable" && (
                        <IconButton onClick={onClose} edge="end" color="inherit" aria-label="close">
                            <FontAwesomeIcon icon={faTimes} size="1x" />
                        </IconButton>
                    )}
                </DialogTitle>
                {children}
            </ErrorBoundaryModal>
        </Drawer>
    )
}

type DocumentDrawerProps = Readonly<{
    name: string
    title: string
    open: boolean
    onClose: () => void
    children: string
}>

const useDocumentDrawerStyles = makeStyles()((theme) => ({
    message: {
        textAlign: "left",
        fontSize: `calc(${theme.typography.body1.fontSize}*1.1)`,
    },
}))

export function DocumentDrawer({ name, title, open, onClose, children }: DocumentDrawerProps) {
    const { classes } = useDocumentDrawerStyles()

    return (
        <StandardDrawer
            name={name}
            title={title}
            open={open}
            onClose={onClose}
            aria-describedby={`${name}-drawer-content`}
        >
            <StandardDrawerContent dividers={false}>
                <DialogContentText
                    id={`${name}-drawer-content`}
                    variant="body1"
                    color="textPrimary"
                    component="div"
                    className={classes.message}
                >
                    <Remark>{children}</Remark>
                </DialogContentText>
            </StandardDrawerContent>
        </StandardDrawer>
    )
}

type StandardSideDrawerProps = Readonly<
    PropsWithChildren<{
        name: string
        title: ReactNode
        subtitle?: ReactNode
        open: boolean
        onClose: () => void
        width?: string
        horizontalPadding?: string
        raised?: boolean
    }>
>

const useStandardSideDrawerStyles = makeStyles<{ width: string; horizontalPadding: string }>()(
    (theme, { width, horizontalPadding }) => ({
        drawerPaper: {
            width,
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
        },
        // Lift the drawer to dialog level so it sits above a full-screen dialog (e.g. the production view)
        raised: {
            zIndex: theme.zIndex.modal,
        },
        header: {
            display: "flex",
            flexDirection: "column",
            padding: `.5rem ${horizontalPadding} 1rem`,
            flexShrink: 0,
        },
        content: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
            padding: `0 ${horizontalPadding} 1rem`,
        },
        titleRow: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
        title: {
            color: theme.palette.text.primary,
        },
        subtitle: {
            color: theme.palette.text.secondary,
        },
        closeButton: {
            color: theme.palette.tailwind.slate[500],
        },
    }),
)

export function StandardSideDrawer({
    name,
    title,
    subtitle,
    open,
    onClose,
    width = "37.5rem",
    horizontalPadding = "1rem",
    raised,
    children,
}: StandardSideDrawerProps) {
    const { classes, cx } = useStandardSideDrawerStyles({ width, horizontalPadding })

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            aria-labelledby={`${name}-side-drawer-title`}
            className={cx(raised && classes.raised)}
            classes={{ paper: classes.drawerPaper }}
        >
            <ErrorBoundaryModal onClose={onClose}>
                <Box className={classes.header}>
                    <Box className={classes.titleRow}>
                        <Typography id={`${name}-side-drawer-title`} variant="h5" className={classes.title}>
                            {title}
                        </Typography>
                        <IconButton onClick={onClose} edge="end" aria-label="close" className={classes.closeButton}>
                            <FontAwesomeIcon icon={faCircleXmark} size="sm" />
                        </IconButton>
                    </Box>
                    {subtitle && (
                        <Typography variant="h6" className={classes.subtitle}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box className={classes.content}>{children}</Box>
            </ErrorBoundaryModal>
        </Drawer>
    )
}
